/* eslint-disable react-hooks/exhaustive-deps */
import { Box, debounce, Stack } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useSearchParamsOperations from "hooks/useSearchParamsOperations";
import useFeatureFlag from "hooks/useFeatureFlag";
import { Location } from "types/location";
import { Pickup } from "types/pickup";
import AssetSearchField from "./AssetSearchField";
import AssetsFiltersMenu from "./AssetsFiltersMenu";

const NEEDS_ATTENTION_FILTER_PREFIX = "needs_attention,is,";
const NEEDS_ATTENTION_FILTER = `${NEEDS_ATTENTION_FILTER_PREFIX}true`;

const ENROLLED_STATUS_OPTIONS = [
  { label: "Enrolled", value: "Enrolled" },
  { label: "Unenrolled", value: "Unenrolled" },
  { label: "Verifying", value: "Verifying Release" },
  { label: "Unable to Release", value: "not_found" },
];

const SECURITY_LOCKS_OPTIONS = [
  { label: "ICloud Locked", value: "Icloud locked" },
  { label: "BIOS Locked", value: "BIOS locked" },
  { label: "EFI Locked", value: "EFI locked" },
  { label: "Activation Locked", value: "Activation lock" },
  { label: "Elevated Security", value: "Elevated security" },
];

function parseFilterValues(searchParams: URLSearchParams, anyOfPrefix: string, singlePrefix: string) {
  const anyOf = searchParams
    .getAll("filter[]")
    .find((p) => p.startsWith(anyOfPrefix))
    ?.slice(anyOfPrefix.length);
  if (anyOf) return anyOf.split(",").filter(Boolean);
  const single = searchParams
    .getAll("filter[]")
    .find((p) => p.startsWith(singlePrefix))
    ?.slice(singlePrefix.length);
  return single ? [single] : [];
}

function sameValues(a: string[], b: string[]) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function parsePickupIds(searchParams: URLSearchParams) {
  return (
    searchParams
      .getAll("filter[]")
      .find((p) => p.includes("pickup_id,is_any_of"))
      ?.split("pickup_id,is_any_of,")[1]
      ?.split(",") || []
  );
}

type AssetsFiltersToolbarProps = {
  pickups: Pickup[];
  locations: Location[];
  pickupListItemRef?: (node: HTMLElement | null) => void;
  onCatalogNeeded?: (catalog: "pickup" | "location") => void;
  pickupsLoading?: boolean;
  locationsLoading?: boolean;
};

export default function AssetsFiltersToolbar({
  pickups,
  locations,
  pickupListItemRef,
  onCatalogNeeded,
  pickupsLoading,
  locationsLoading,
}: AssetsFiltersToolbarProps) {
  const [searchParams] = useSearchParams();
  const { replace, deleteParam, deleteFieldInArrayUsingPrefix, replaceInArrayFieldUsingPrefix } =
    useSearchParamsOperations();
  const { enabled: enrollmentV2Enabled } = useFeatureFlag("enrollment_v2");

  const [pickupIds, setPickupIds] = useState(() => parsePickupIds(searchParams));
  const [locationIds, setLocationIds] = useState(() =>
    parseFilterValues(searchParams, "location_id,is_any_of,", "location_id,is,")
  );
  const [enrolledStatuses, setEnrolledStatuses] = useState(() =>
    parseFilterValues(searchParams, "enrolled_status,is_any_of,", "enrolled_status,is,")
  );
  const [securityLevels, setSecurityLevels] = useState(() =>
    parseFilterValues(searchParams, "security_level,is_any_of,", "security_level,is,")
  );

  useEffect(() => {
    const nextPickupIds = parsePickupIds(searchParams);
    const nextLocationIds = parseFilterValues(searchParams, "location_id,is_any_of,", "location_id,is,");
    const nextEnrolledStatuses = parseFilterValues(searchParams, "enrolled_status,is_any_of,", "enrolled_status,is,");
    const nextSecurityLevels = parseFilterValues(searchParams, "security_level,is_any_of,", "security_level,is,");

    setPickupIds((prev) => (sameValues(prev, nextPickupIds) ? prev : nextPickupIds));
    setLocationIds((prev) => (sameValues(prev, nextLocationIds) ? prev : nextLocationIds));
    setEnrolledStatuses((prev) => (sameValues(prev, nextEnrolledStatuses) ? prev : nextEnrolledStatuses));
    setSecurityLevels((prev) => (sameValues(prev, nextSecurityLevels) ? prev : nextSecurityLevels));
  }, [searchParams]);

  const needsAttentionFilter = useMemo(
    () => searchParams.getAll("filter[]").some((filter) => filter.startsWith(NEEDS_ATTENTION_FILTER_PREFIX)),
    [searchParams]
  );

  const urlQuery = searchParams.get("q") || "";
  const [query, setQuery] = useState(urlQuery);
  const pendingQuery = useRef<string | null>(null);

  const handleSearchChange = useCallback(
    debounce((replaceParamsFn: (f: string, v: string) => void, search: string) => {
      replaceParamsFn("q", search);
    }, 200),
    []
  );

  useEffect(() => () => handleSearchChange.clear(), [handleSearchChange]);

  useEffect(() => {
    // Ignore q until the debounced write catches up, so a late echo cannot reset the field.
    if (pendingQuery.current !== null) {
      if (urlQuery === pendingQuery.current) pendingQuery.current = null;
      return;
    }

    setQuery((current) => (current === urlQuery ? current : urlQuery));
  }, [urlQuery]);

  const cleanFilters = useCallback(() => {
    setPickupIds([]);
    setLocationIds([]);
    setEnrolledStatuses([]);
    setSecurityLevels([]);
    deleteParam("filter[]");
    deleteParam("filter_link_operator");
  }, [deleteParam]);

  const handleNeedsAttentionChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        replaceInArrayFieldUsingPrefix("filter[]", NEEDS_ATTENTION_FILTER_PREFIX, NEEDS_ATTENTION_FILTER);
      } else {
        deleteFieldInArrayUsingPrefix("filter[]", NEEDS_ATTENTION_FILTER_PREFIX);
      }
    },
    [replaceInArrayFieldUsingPrefix, deleteFieldInArrayUsingPrefix]
  );

  useEffect(() => {
    // Asset lifecycle Status is not part of the new filters UI.
    deleteFieldInArrayUsingPrefix("filter[]", "status,is,");
    deleteFieldInArrayUsingPrefix("filter[]", "status,is_any_of");
  }, []);

  useEffect(() => {
    if (pickupIds.length > 0) {
      replaceInArrayFieldUsingPrefix("filter[]", "pickup_id,is_any_of", `pickup_id,is_any_of,${pickupIds.join(",")}`);
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "pickup_id,is_any_of");
    }
  }, [pickupIds]);

  useEffect(() => {
    deleteFieldInArrayUsingPrefix("filter[]", "location_id,is,");
    if (locationIds.length > 0) {
      replaceInArrayFieldUsingPrefix(
        "filter[]",
        "location_id,is_any_of",
        `location_id,is_any_of,${locationIds.join(",")}`
      );
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "location_id,is_any_of");
    }
  }, [locationIds]);

  useEffect(() => {
    deleteFieldInArrayUsingPrefix("filter[]", "enrolled_status,is,");
    if (enrolledStatuses.length > 0) {
      replaceInArrayFieldUsingPrefix(
        "filter[]",
        "enrolled_status,is_any_of",
        `enrolled_status,is_any_of,${enrolledStatuses.join(",")}`
      );
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "enrolled_status,is_any_of");
    }
  }, [enrolledStatuses]);

  useEffect(() => {
    deleteFieldInArrayUsingPrefix("filter[]", "security_level,is,");
    if (securityLevels.length > 0) {
      replaceInArrayFieldUsingPrefix(
        "filter[]",
        "security_level,is_any_of",
        `security_level,is_any_of,${securityLevels.join(",")}`
      );
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "security_level,is_any_of");
    }
  }, [securityLevels]);

  return (
    <Box sx={{ maxWidth: { sm: 720 } }}>
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0, width: "100%" }}>
        <AssetSearchField
          value={query}
          onChange={(search) => {
            pendingQuery.current = search === urlQuery ? null : search;
            setQuery(search);
            handleSearchChange(replace, search);
          }}
        />
        <AssetsFiltersMenu
          needsAttention={needsAttentionFilter}
          onNeedsAttentionChange={handleNeedsAttentionChange}
          enrolledStatuses={enrolledStatuses}
          onEnrolledStatusesChange={setEnrolledStatuses}
          enrolledStatusOptions={ENROLLED_STATUS_OPTIONS}
          securityLevels={securityLevels}
          onSecurityLevelsChange={setSecurityLevels}
          securityLocksOptions={SECURITY_LOCKS_OPTIONS}
          pickupIds={pickupIds}
          onPickupIdsChange={setPickupIds}
          pickups={pickups}
          locationIds={locationIds}
          onLocationIdsChange={setLocationIds}
          locations={locations}
          onClearAll={cleanFilters}
          pickupListItemRef={pickupListItemRef}
          enrollmentV2Enabled={enrollmentV2Enabled}
          onCatalogNeeded={onCatalogNeeded}
          pickupsLoading={pickupsLoading}
          locationsLoading={locationsLoading}
        />
      </Stack>
    </Box>
  );
}
